<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- HEADER -->
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-heart-pulse me-2"></i>{{ $t('page.vitals', 'Vitals & NEWS2') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-amber" @click="autoRefresh = !autoRefresh">
            <i class="mdi" :class="autoRefresh ? 'mdi-pause' : 'mdi-play'"></i>
            {{ autoRefresh ? 'Pause' : 'Auto-refresh' }}
          </button>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-danger">{{ stats.high_count || 0 }}</div>
          <div class="hms-stat-lbl">High Risk (≥7)</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-warning">{{ stats.medium_count || 0 }}</div>
          <div class="hms-stat-lbl">Medium Risk (5-6)</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-info">{{ stats.single3_count || 0 }}</div>
          <div class="hms-stat-lbl">Single Param 3</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-warning">{{ stats.pending_escalation || 0 }}</div>
          <div class="hms-stat-lbl">Pending Escalation</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-success">{{ stats.recorded_today || 0 }}</div>
          <div class="hms-stat-lbl">Recorded Today</div>
        </div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='watch' }" @click="tab='watch'">
          <i class="mdi mdi-eye-outline me-1"></i>Watchlist
        </button>
        <button class="hms-tab" :class="{ active: tab==='record' }" @click="tab='record'">
          <i class="mdi mdi-pencil-plus me-1"></i>Record New
        </button>
        <button class="hms-tab" :class="{ active: tab==='trend' }" @click="tab='trend'">
          <i class="mdi mdi-chart-line me-1"></i>Patient Trend
        </button>
      </div>

      <!-- ============ WATCHLIST TAB ============ -->
      <div v-if="tab==='watch'">
        <div v-if="!watchRows.length" class="hms-empty-state">
          <i class="mdi mdi-check-circle"></i>
          <h5>No active admissions</h5>
          <p>Watchlist will populate as inpatients are admitted and vitals are recorded.</p>
        </div>
        <div v-else class="hms-watch-list">
          <div v-for="r in watchRows" :key="r.admission_id" class="hms-watch-card" :class="bandClass(r.news2_band)">
            <div class="hms-watch-score">
              <div class="hms-watch-num">{{ r.news2_score != null ? r.news2_score : '—' }}</div>
              <div class="hms-watch-band">{{ bandLabel(r.news2_band) }}</div>
            </div>
            <div class="hms-watch-pt">
              <div class="hms-watch-name">{{ r.patient_name }}</div>
              <div class="hms-watch-sub">
                <span v-if="r.patient_no">{{ r.patient_no }}</span>
                <span v-if="r.ward_name"> · {{ r.ward_name }}</span>
                <span v-if="r.bed_number"> · Bed {{ r.bed_number }}</span>
              </div>
              <div v-if="r.recorded_at" class="hms-watch-when">
                Last obs {{ formatAgo(r.minutes_since) }} ago
                ({{ formatDateTime(r.recorded_at) }})
              </div>
              <div v-else class="hms-watch-noobs">No vitals recorded yet</div>
            </div>
            <div class="hms-watch-vitals" v-if="r.vital_id">
              <div><span class="hms-vk">BP</span> {{ r.bp_systolic || '—' }}/{{ r.bp_diastolic || '—' }}</div>
              <div><span class="hms-vk">HR</span> {{ r.heart_rate || '—' }}</div>
              <div><span class="hms-vk">RR</span> {{ r.respiratory_rate || '—' }}</div>
              <div><span class="hms-vk">SpO₂</span> {{ r.spo2 != null ? r.spo2 + '%' : '—' }}</div>
              <div><span class="hms-vk">T°</span> {{ r.temperature || '—' }}</div>
              <div><span class="hms-vk">AVPU</span> {{ r.avpu || 'A' }}</div>
            </div>
            <div class="hms-watch-actions">
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="quickRecord(r)">
                <i class="mdi mdi-plus"></i> Record
              </button>
              <button v-if="r.news2_band === 'high' || r.news2_band === 'medium'" class="hms-btn hms-btn-amber hms-btn-sm" @click="acknowledge(r)">
                <i class="mdi mdi-check"></i> Acknowledge
              </button>
              <button class="hms-btn hms-btn-link hms-btn-sm" @click="openTrend(r)">
                <i class="mdi mdi-chart-line"></i> Trend
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ RECORD TAB ============ -->
      <div v-if="tab==='record'">
        <div class="hms-card">
          <div class="hms-card-head">Record observation set</div>
          <div class="hms-card-body">
            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="hms-label">Patient <span class="text-danger">*</span></label>
                <div class="d-flex gap-2">
                  <input v-model="recPatientSearch" class="hms-input" placeholder="Search by name or patient no" @keyup.enter="findPatient" />
                  <button class="hms-btn hms-btn-blue" @click="findPatient">Find</button>
                </div>
                <div v-if="recPatient" class="hms-patient-chip">
                  <i class="mdi mdi-account"></i>
                  {{ recPatient.first_name }} {{ recPatient.last_name }} ({{ recPatient.patient_no }})
                  <a href="javascript:void(0)" @click="recPatient = null; recAdmissionId = ''" class="ms-2">change</a>
                </div>
              </div>
              <div class="col-md-6" v-if="recAdmissions.length">
                <label class="hms-label">Active admission</label>
                <select v-model="recAdmissionId" class="hms-input">
                  <option value="">— outpatient —</option>
                  <option v-for="a in recAdmissions" :key="a.admission_id" :value="a.admission_id">
                    {{ a.admission_number }} ({{ a.ward_name || '—' }})
                  </option>
                </select>
              </div>
            </div>

            <div class="row g-3">
              <div class="col-md-3 col-6">
                <label class="hms-label">BP Systolic <span class="hms-unit">mmHg</span></label>
                <input v-model.number="form.bp_systolic" type="number" class="hms-input" :class="vitalClass('bp', form.bp_systolic)" />
              </div>
              <div class="col-md-3 col-6">
                <label class="hms-label">BP Diastolic <span class="hms-unit">mmHg</span></label>
                <input v-model.number="form.bp_diastolic" type="number" class="hms-input" />
              </div>
              <div class="col-md-3 col-6">
                <label class="hms-label">Heart Rate <span class="hms-unit">bpm</span></label>
                <input v-model.number="form.heart_rate" type="number" class="hms-input" :class="vitalClass('hr', form.heart_rate)" />
              </div>
              <div class="col-md-3 col-6">
                <label class="hms-label">Resp Rate <span class="hms-unit">/min</span></label>
                <input v-model.number="form.respiratory_rate" type="number" class="hms-input" :class="vitalClass('rr', form.respiratory_rate)" />
              </div>
              <div class="col-md-3 col-6">
                <label class="hms-label">SpO₂ <span class="hms-unit">%</span></label>
                <input v-model.number="form.spo2" type="number" min="0" max="100" class="hms-input" :class="vitalClass('spo2', form.spo2)" />
              </div>
              <div class="col-md-3 col-6">
                <label class="hms-label">Temperature <span class="hms-unit">°C</span></label>
                <input v-model.number="form.temperature" type="number" step="0.1" class="hms-input" :class="vitalClass('t', form.temperature)" />
              </div>
              <div class="col-md-3 col-6">
                <label class="hms-label">AVPU</label>
                <select v-model="form.avpu" class="hms-input" :class="form.avpu !== 'A' ? 'st-bad' : ''">
                  <option value="A">A — Alert</option>
                  <option value="C">C — New Confusion</option>
                  <option value="V">V — Verbal</option>
                  <option value="P">P — Pain</option>
                  <option value="U">U — Unresponsive</option>
                </select>
              </div>
              <div class="col-md-3 col-6">
                <label class="hms-label">SpO₂ Scale</label>
                <select v-model.number="form.spo2_scale" class="hms-input">
                  <option :value="1">Scale 1 (target ≥96%)</option>
                  <option :value="2">Scale 2 (hypercapnic, target 88-92%)</option>
                </select>
              </div>
              <div class="col-md-3 col-6">
                <label class="hms-label">On Oxygen?</label>
                <div class="d-flex gap-3 align-items-center pt-2">
                  <label class="form-check-label"><input type="radio" v-model.number="form.on_oxygen" :value="0" class="me-1" />Room air</label>
                  <label class="form-check-label"><input type="radio" v-model.number="form.on_oxygen" :value="1" class="me-1" />Supplemental</label>
                </div>
              </div>
              <div class="col-md-3 col-6" v-if="form.on_oxygen == 1">
                <label class="hms-label">O₂ <span class="hms-unit">L/min</span></label>
                <input v-model.number="form.oxygen_lpm" type="number" step="0.1" class="hms-input" />
              </div>
              <div class="col-md-3 col-6">
                <label class="hms-label">Pain Score (0-10)</label>
                <input v-model.number="form.pain_score" type="number" min="0" max="10" class="hms-input" />
              </div>
              <div class="col-md-3 col-6">
                <label class="hms-label">Glucose <span class="hms-unit">mmol/L</span></label>
                <input v-model.number="form.glucose_mmol" type="number" step="0.1" class="hms-input" />
              </div>
              <div class="col-md-3 col-6">
                <label class="hms-label">Weight <span class="hms-unit">kg</span></label>
                <input v-model.number="form.weight_kg" type="number" step="0.1" class="hms-input" />
              </div>
              <div class="col-md-3 col-6">
                <label class="hms-label">Height <span class="hms-unit">cm</span></label>
                <input v-model.number="form.height_cm" type="number" step="0.1" class="hms-input" />
              </div>
              <div class="col-12">
                <label class="hms-label">Notes</label>
                <textarea v-model="form.notes" class="hms-input" rows="2" placeholder="Observations, interventions, response…"></textarea>
              </div>
            </div>

            <!-- Live NEWS2 preview -->
            <div class="hms-news-preview" :class="bandClass(previewBand)">
              <div class="hms-news-num">
                <span class="hms-news-label">NEWS2</span>
                <span class="hms-news-val">{{ previewScore }}</span>
              </div>
              <div class="hms-news-band">{{ bandLabel(previewBand) }}</div>
              <div class="hms-news-action">{{ bandAction(previewBand) }}</div>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-3">
              <button class="hms-btn hms-btn-secondary" @click="resetForm">Reset</button>
              <button class="hms-btn hms-btn-success" @click="save" :disabled="saving || !recPatient">
                <i class="mdi mdi-content-save"></i> {{ saving ? 'Saving…' : 'Save Vitals' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ TREND TAB ============ -->
      <div v-if="tab==='trend'">
        <div class="hms-card">
          <div class="hms-card-head d-flex justify-content-between">
            <span>{{ trendPatient ? trendPatient.patient_name : 'Patient trend' }}</span>
            <div class="d-flex gap-2">
              <input v-model="trendSearch" class="hms-input hms-input-sm" placeholder="Patient name / no" @keyup.enter="findTrendPatient" />
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="findTrendPatient">Load</button>
            </div>
          </div>
          <div class="hms-card-body">
            <div v-if="!trendRows.length" class="hms-muted hms-pad text-center">
              {{ trendSearch ? 'No vitals on record.' : 'Search a patient to view their NEWS2 trend.' }}
            </div>
            <div v-else>
              <table class="hms-table">
                <thead>
                  <tr>
                    <th>Recorded</th>
                    <th>BP</th>
                    <th>HR</th>
                    <th>RR</th>
                    <th>SpO₂</th>
                    <th>T°</th>
                    <th>AVPU</th>
                    <th>O₂</th>
                    <th>NEWS2</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="v in trendRows" :key="v.vital_id" :class="bandRowClass(v.news2_band)">
                    <td>{{ formatDateTime(v.recorded_at) }}</td>
                    <td>{{ v.bp_systolic || '—' }}/{{ v.bp_diastolic || '—' }}</td>
                    <td>{{ v.heart_rate || '—' }}</td>
                    <td>{{ v.respiratory_rate || '—' }}</td>
                    <td>{{ v.spo2 != null ? v.spo2 + '%' : '—' }}</td>
                    <td>{{ v.temperature || '—' }}</td>
                    <td>{{ v.avpu || 'A' }}</td>
                    <td>{{ v.on_oxygen == 1 ? (v.oxygen_lpm || 'O₂') : 'air' }}</td>
                    <td>
                      <span class="hms-news-pill" :class="bandClass(v.news2_band)">
                        {{ v.news2_score }} · {{ bandLabel(v.news2_band) }}
                      </span>
                    </td>
                    <td>{{ v.recorded_by_name || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "VitalsCharting",
  components: { Navigation },
  data() {
    return {
      tab: 'watch',
      autoRefresh: true,
      loading: false,
      saving: false,
      refreshTimer: null,

      stats: {},
      watchRows: [],

      recPatientSearch: '',
      recPatient: null,
      recAdmissions: [],
      recAdmissionId: '',
      form: this.blankForm(),

      trendSearch: '',
      trendPatient: null,
      trendRows: []
    };
  },
  computed: {
    previewScore() { return this.computeNews2(this.form).score; },
    previewBand()  { return this.computeNews2(this.form).band;  }
  },
  mounted() {
    this.loadAll();
    this.refreshTimer = setInterval(() => { if (this.autoRefresh && this.tab === 'watch') this.loadAll(true); }, 60000);
  },
  beforeUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  },
  methods: {
    blankForm() {
      return {
        bp_systolic: null, bp_diastolic: null, heart_rate: null,
        respiratory_rate: null, spo2: null, temperature: null,
        avpu: 'A', spo2_scale: 1, on_oxygen: 0, oxygen_lpm: null,
        pain_score: null, weight_kg: null, height_cm: null, glucose_mmol: null,
        notes: ''
      };
    },
    resetForm() { this.form = this.blankForm(); },

    async loadAll(silent) {
      if (!silent) this.loading = true;
      try {
        const r = await executeJson({ function: 'getNews2Watchlist' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.watchRows = d.Watchlist || [];
          this.stats     = d.Stats     || {};
        }
      } catch (e) { console.error(e); }
      this.loading = false;
    },

    async findPatient() {
      const q = (this.recPatientSearch || '').trim();
      if (!q) return;
      try {
        const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          const p = (d.Patients || d.patients || [])[0];
          if (!p) { alert('No patient found.'); return; }
          this.recPatient = p;
          // Look for active admission
          const a = await executeJson({ function: 'getAdmissions', patient_id: p.patient_id, status: 'admitted' }, hms);
          if (a.data && a.data.success) {
            const ad = a.data.data || {};
            this.recAdmissions = ad.Admissions || ad.admissions || [];
            this.recAdmissionId = this.recAdmissions[0] ? this.recAdmissions[0].admission_id : '';
          }
        }
      } catch (e) { console.error(e); alert('Search failed.'); }
    },

    quickRecord(r) {
      this.tab = 'record';
      this.recPatient = { patient_id: r.patient_id, patient_no: r.patient_no, first_name: (r.patient_name || '').split(' ')[0], last_name: (r.patient_name || '').split(' ').slice(1).join(' ') };
      this.recAdmissions = [{ admission_id: r.admission_id, admission_number: r.admission_number, ward_name: r.ward_name }];
      this.recAdmissionId = r.admission_id;
      this.resetForm();
    },

    async save() {
      if (!this.recPatient) { alert('Select a patient.'); return; }
      this.saving = true;
      try {
        const params = {
          function: 'saveVitals',
          patient_id:   this.recPatient.patient_id,
          admission_id: this.recAdmissionId || '',
          ...this.form
        };
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) {
          alert('Saved. NEWS2 = ' + (r.data.data && r.data.data.news2));
          this.resetForm();
          this.tab = 'watch';
          await this.loadAll();
        } else {
          alert((r.data && r.data.message) || 'Save failed.');
        }
      } catch (e) { console.error(e); alert('Save failed.'); }
      finally { this.saving = false; }
    },

    async acknowledge(r) {
      const note = prompt('Acknowledgement note (action taken)?', '');
      if (note === null) return;
      try {
        const ack = await executeJson({ function: 'acknowledgeVitalsEscalation', vital_id: r.vital_id, notes: note }, hms);
        if (ack.data && ack.data.success) await this.loadAll();
      } catch (e) { console.error(e); }
    },

    openTrend(r) {
      this.tab = 'trend';
      this.trendSearch = r.patient_no || r.patient_name;
      this.trendPatient = { patient_id: r.patient_id, patient_name: r.patient_name };
      this.loadTrend(r.patient_id);
    },
    async findTrendPatient() {
      const q = (this.trendSearch || '').trim();
      if (!q) return;
      try {
        const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          const p = (d.Patients || d.patients || [])[0];
          if (!p) { alert('No patient found.'); this.trendRows = []; return; }
          this.trendPatient = { patient_id: p.patient_id, patient_name: (p.first_name || '') + ' ' + (p.last_name || '') };
          await this.loadTrend(p.patient_id);
        }
      } catch (e) { console.error(e); }
    },
    async loadTrend(patientId) {
      try {
        const r = await executeJson({ function: 'getVitalsForPatient', patient_id: patientId, hours: 168 }, hms);
        if (r.data && r.data.success) {
          this.trendRows = (r.data.data && r.data.data.Vitals) || [];
        }
      } catch (e) { console.error(e); }
    },

    // ===== NEWS2 helpers (mirror of backend, for live preview) =====
    computeNews2(v) {
      let rrS = 0, spS = 0, o2S = 0, sbpS = 0, hrS = 0, tS = 0, aS = 0;
      const rr = v.respiratory_rate;
      if (rr != null && rr !== '') {
        if (rr <= 8) rrS = 3; else if (rr <= 11) rrS = 1; else if (rr <= 20) rrS = 0; else if (rr <= 24) rrS = 2; else rrS = 3;
      }
      const sp = v.spo2; const scale = v.spo2_scale || 1; const onO2 = v.on_oxygen ? 1 : 0;
      if (sp != null && sp !== '') {
        if (scale === 2) {
          if (sp <= 83) spS = 3; else if (sp <= 85) spS = 2; else if (sp <= 87) spS = 1;
          else if (sp <= 92) spS = 0; else if (!onO2) spS = 0; else if (sp <= 94) spS = 1;
          else if (sp <= 96) spS = 2; else spS = 3;
        } else {
          if (sp <= 91) spS = 3; else if (sp <= 93) spS = 2; else if (sp <= 95) spS = 1; else spS = 0;
        }
      }
      o2S = onO2 ? 2 : 0;
      const sbp = v.bp_systolic;
      if (sbp != null && sbp !== '') {
        if (sbp <= 90) sbpS = 3; else if (sbp <= 100) sbpS = 2; else if (sbp <= 110) sbpS = 1; else if (sbp <= 219) sbpS = 0; else sbpS = 3;
      }
      const hr = v.heart_rate;
      if (hr != null && hr !== '') {
        if (hr <= 40) hrS = 3; else if (hr <= 50) hrS = 1; else if (hr <= 90) hrS = 0; else if (hr <= 110) hrS = 1; else if (hr <= 130) hrS = 2; else hrS = 3;
      }
      const t = v.temperature;
      if (t != null && t !== '') {
        if (t <= 35.0) tS = 3; else if (t <= 36.0) tS = 1; else if (t <= 38.0) tS = 0; else if (t <= 39.0) tS = 1; else tS = 2;
      }
      aS = (v.avpu && v.avpu !== 'A') ? 3 : 0;
      const total = rrS + spS + o2S + sbpS + hrS + tS + aS;
      const has3 = (rrS === 3 || spS === 3 || sbpS === 3 || hrS === 3 || tS === 3 || aS === 3);
      let band;
      if (total >= 7) band = 'high';
      else if (total >= 5) band = 'medium';
      else if (has3) band = 'low_single3';
      else if (total >= 1) band = 'low';
      else band = 'none';
      return { score: total, band };
    },

    bandClass(b) {
      return ({ none: 'st-none', low: 'st-low', low_single3: 'st-single3', medium: 'st-medium', high: 'st-high' })[b] || 'st-none';
    },
    bandRowClass(b) {
      return ({ high: 'row-high', medium: 'row-medium', low_single3: 'row-single3' })[b] || '';
    },
    bandLabel(b) {
      return ({ none: 'OK', low: 'LOW', low_single3: 'SINGLE 3', medium: 'MEDIUM', high: 'HIGH' })[b] || '—';
    },
    bandAction(b) {
      return ({
        none:        '12-hourly observations',
        low:         'Min 4-6 hourly. Inform nurse-in-charge.',
        low_single3: 'Min 1-hourly. Urgent ward review.',
        medium:      'Min 1-hourly. Doctor must review within 1 hour.',
        high:        'Continuous monitoring. Senior clinician + critical care now.'
      })[b] || '';
    },
    vitalClass(kind, val) {
      if (val == null || val === '') return '';
      if (kind === 'rr')   return (val <= 11 || val >= 21) ? 'st-bad' : '';
      if (kind === 'hr')   return (val <= 50 || val >= 91) ? 'st-bad' : '';
      if (kind === 'bp')   return (val <= 110 || val >= 220) ? 'st-bad' : '';
      if (kind === 'spo2') return (val <= 95) ? 'st-bad' : '';
      if (kind === 't')    return (val <= 36 || val >= 38.1) ? 'st-bad' : '';
      return '';
    },

    formatAgo(m) {
      if (m == null) return '—';
      if (m < 60) return Math.round(m) + 'm';
      const h = Math.floor(m / 60);
      return h + 'h ' + Math.round(m - h * 60) + 'm';
    },
    formatDateTime(s) {
      if (!s) return '—';
      const d = new Date(s.replace ? s.replace(' ', 'T') : s);
      return isNaN(d) ? s : d.toLocaleString();
    }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; text-decoration: none; font-size: 20px; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 28px; font-weight: 700; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-danger  { color: #c53030; }
.hms-stat-warning { color: #c05621; }
.hms-stat-info    { color: #2c5282; }
.hms-stat-success { color: #276749; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-watch-list { display: flex; flex-direction: column; gap: 10px; }
.hms-watch-card { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #cbd5e0; border-radius: 8px; padding: 14px; display: grid; grid-template-columns: 110px 1fr 1.2fr auto; gap: 16px; align-items: center; }
.hms-watch-card.st-high   { border-left-color: #e53e3e; background: #fff5f5; }
.hms-watch-card.st-medium { border-left-color: #dd6b20; background: #fffaf0; }
.hms-watch-card.st-single3{ border-left-color: #d69e2e; background: #fffff0; }
.hms-watch-card.st-low    { border-left-color: #4299e1; }
.hms-watch-card.st-none   { border-left-color: #38a169; }

.hms-watch-score { text-align: center; }
.hms-watch-num   { font-size: 36px; font-weight: 700; line-height: 1; color: #1a202c; }
.hms-watch-band  { font-size: 11px; font-weight: 700; text-transform: uppercase; color: #4a5568; margin-top: 4px; letter-spacing: .5px; }
.hms-watch-card.st-high   .hms-watch-band { color: #c53030; }
.hms-watch-card.st-medium .hms-watch-band { color: #c05621; }

.hms-watch-name { font-weight: 600; color: #1a202c; }
.hms-watch-sub  { font-size: 12px; color: #718096; }
.hms-watch-when { font-size: 12px; color: #4a5568; margin-top: 4px; }
.hms-watch-noobs{ font-size: 12px; color: #c53030; margin-top: 4px; font-style: italic; }
.hms-watch-vitals { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px 12px; font-size: 12px; }
.hms-vk { display: inline-block; min-width: 36px; color: #a0aec0; font-size: 10px; text-transform: uppercase; }
.hms-watch-actions { display: flex; flex-direction: column; gap: 4px; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 12px 16px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 16px; }
.hms-label { font-size: 12px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 4px; }
.hms-unit  { color: #a0aec0; font-weight: 400; font-size: 11px; }
.hms-input { width: 100%; padding: 7px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 14px; }
.hms-input.hms-input-sm { padding: 4px 8px; font-size: 12px; }
.hms-input.st-bad { border-color: #fc8181; background: #fff5f5; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; margin-top: 6px; font-size: 13px; }

.hms-news-preview { margin-top: 18px; border-radius: 8px; padding: 14px 18px; display: flex; align-items: center; gap: 24px; border: 2px solid #e2e8f0; }
.hms-news-preview.st-none    { background: #f0fff4; border-color: #9ae6b4; }
.hms-news-preview.st-low     { background: #ebf8ff; border-color: #90cdf4; }
.hms-news-preview.st-single3 { background: #fffff0; border-color: #ecc94b; }
.hms-news-preview.st-medium  { background: #fffaf0; border-color: #f6ad55; }
.hms-news-preview.st-high    { background: #fff5f5; border-color: #fc8181; }
.hms-news-num { display: flex; flex-direction: column; align-items: center; }
.hms-news-label { font-size: 10px; color: #718096; letter-spacing: 1px; }
.hms-news-val   { font-size: 36px; font-weight: 700; line-height: 1; color: #1a202c; }
.hms-news-band  { font-size: 16px; font-weight: 700; text-transform: uppercase; color: #2d3748; }
.hms-news-action{ font-size: 13px; color: #4a5568; flex: 1; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.hms-table th { background: #f7fafc; text-align: left; padding: 10px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; }
.hms-table td { padding: 8px 10px; border-bottom: 1px solid #edf2f7; }
.hms-table tr.row-high   { background: #fff5f5; }
.hms-table tr.row-medium { background: #fffaf0; }
.hms-table tr.row-single3{ background: #fffff0; }

.hms-news-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 700; }
.hms-news-pill.st-none    { background: #c6f6d5; color: #22543d; }
.hms-news-pill.st-low     { background: #bee3f8; color: #2a4365; }
.hms-news-pill.st-single3 { background: #faf089; color: #744210; }
.hms-news-pill.st-medium  { background: #feebc8; color: #7b341e; }
.hms-news-pill.st-high    { background: #fed7d7; color: #742a2a; }

.hms-empty-state { background: #fff; border: 1px dashed #cbd5e0; border-radius: 8px; padding: 48px; text-align: center; color: #718096; }
.hms-empty-state .mdi { font-size: 48px; color: #38a169; }
.hms-pad { padding: 24px; }
.hms-muted { color: #a0aec0; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-amber     { background: #d69e2e; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border-color: #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; }

.hms-page-header { padding-top: 12px; }
.hms-page-title  { font-size: 18px; font-weight: 600; color: #2d3748; }
</style>
